// TunerPro-compatible XDF (definition) parser + BIN map reader/writer.
// Supports XDFTABLE (2D/1D maps), XDFCONSTANT, axis labels (LABEL text/number,
// embedded address, or linked object via embedinfo), linear MATH conversion, and
// endianness auto-detected from mmedtypeflags bit 0x02 (LSB-first / little-endian).

const attr = (el, name, def = null) => (el && el.getAttribute(name) != null ? el.getAttribute(name) : def);
const intAttr = (el, name, def = 0) => {
  const v = attr(el, name);
  if (v == null) return def;
  const s = String(v).trim();
  return s.toLowerCase().startsWith("0x") ? parseInt(s, 16) : parseInt(s, 10);
};

const isSigned = (flags) => (flags & 0x01) !== 0;
const endianOf = (flags) => (flags & 0x02) !== 0; // bit 0x02 = LSB-first (little-endian)
const colMajorOf = (flags) => (flags & 0x04) !== 0; // bit 0x04 = column-major cell order
const normId = (v) => (v == null ? null : String(v).toLowerCase());

// Sandboxed arithmetic evaluator for XDF conversion equations — no eval/new Function.
// Supports the variable x, decimal/exponent numbers, + - * /, unary minus, parentheses.
function compileArith(expr) {
  const src = String(expr).replace(/\bX\b/gi, "x");
  const tokens = [];
  const re = /\s*([0-9]*\.?[0-9]+(?:[eE][+-]?[0-9]+)?|[xX]|[+\-*/()])/g;
  let m, last = 0;
  while ((m = re.exec(src)) !== null) {
    if (m.index !== last) return null;
    tokens.push(m[1]);
    last = re.lastIndex;
  }
  if (src.slice(last).trim() !== "") return null;
  const out = [], ops = [];
  const prec = { "+": 1, "-": 1, "*": 2, "/": 2, "u-": 3 };
  let prev = null;
  for (const t of tokens) {
    if (/^[0-9.]/.test(t)) { out.push(parseFloat(t)); prev = "num"; }
    else if (t === "x" || t === "X") { out.push("x"); prev = "num"; }
    else if (t === "(") { ops.push(t); prev = "("; }
    else if (t === ")") {
      while (ops.length && ops[ops.length - 1] !== "(") out.push(ops.pop());
      if (!ops.length) return null;
      ops.pop(); prev = "num";
    } else {
      let op = t;
      if (t === "-" && (prev === null || prev === "(" || prev === "op")) op = "u-";
      while (op !== "u-" && ops.length && ops[ops.length - 1] !== "(" && prec[ops[ops.length - 1]] >= prec[op]) out.push(ops.pop());
      ops.push(op); prev = "op";
    }
  }
  while (ops.length) { const o = ops.pop(); if (o === "(") return null; out.push(o); }
  return (x) => {
    const st = [];
    for (const t of out) {
      if (t === "x") st.push(x);
      else if (typeof t === "number") st.push(t);
      else if (t === "u-") st.push(-st.pop());
      else { const b = st.pop(), a = st.pop(); st.push(t === "+" ? a + b : t === "-" ? a - b : t === "*" ? a * b : a / b); }
    }
    return st.length === 1 ? st[0] : NaN;
  };
}

function makeEquation(eq) {
  if (!eq) return { fwd: (x) => x, a: 1, b: 0, linear: true };
  const fn = compileArith(eq);
  const fwd = fn ? (x) => { const r = fn(x); return Number.isFinite(r) ? r : x; } : (x) => x;
  const b = fwd(0);
  const a = fwd(1) - b;
  const linear = Number.isFinite(a) && a !== 0;
  return { fwd, a, b, linear };
}

function parseAxis(axisEl) {
  if (!axisEl) return null;
  const ed = axisEl.querySelector("EMBEDDEDDATA");
  const labels = Array.from(axisEl.querySelectorAll("LABEL")).map((l) => attr(l, "value"));
  const mathEls = Array.from(axisEl.querySelectorAll("MATH"));
  let equation = "X";
  const eqOverrides = [];
  mathEls.forEach((m) => {
    const es = attr(m, "equation", "X");
    const rw = m.getAttribute("row"); const cl = m.getAttribute("col");
    if (rw == null && cl == null) { equation = es; }
    else { eqOverrides.push({ row: rw != null ? parseInt(rw, 10) : null, col: cl != null ? parseInt(cl, 10) : null, eq: makeEquation(es) }); }
  });
  const embed = axisEl.querySelector("embedinfo");
  const idxEl = axisEl.querySelector("indexcount");
  const otEl = axisEl.querySelector("outputtype");
  const dpEl = axisEl.querySelector("decimalpl");
  return {
    id: attr(axisEl, "id"),
    outputType: otEl ? (parseInt(otEl.textContent, 10) || 1) : 1,
    decimals: dpEl && dpEl.textContent.trim() !== "" ? (parseInt(dpEl.textContent, 10) || 0) : null,
    address: ed ? intAttr(ed, "mmedaddress", null) : null,
    elemBits: ed ? intAttr(ed, "mmedelementsizebits", 8) : 8,
    rowCount: ed ? intAttr(ed, "mmedrowcount", 0) : 0,
    colCount: ed ? intAttr(ed, "mmedcolcount", 0) : 0,
    majorStride: ed ? intAttr(ed, "mmedmajorstridebits", 0) : 0,
    minorStride: ed ? intAttr(ed, "mmedminorstridebits", 0) : 0,
    typeFlags: ed ? intAttr(ed, "mmedtypeflags", 0) : 0,
    labels,
    indexcount: idxEl ? parseInt(idxEl.textContent, 10) || 0 : 0,
    linkObjId: embed ? normId(attr(embed, "linkobjid", null)) : null,
    min: axisEl.querySelector("min") ? parseFloat(axisEl.querySelector("min").textContent) : null,
    max: axisEl.querySelector("max") ? parseFloat(axisEl.querySelector("max").textContent) : null,
    eq: makeEquation(equation),
    equationStr: equation,
    eqOverrides,
  };
}

function categoryName(el, categories) {
  const mem = el.querySelector("CATEGORYMEM");
  if (mem) {
    const c = intAttr(mem, "category", 1) - 1;
    if (categories[c] != null) return categories[c];
  }
  return "Uncategorized";
}

// Ordered category names from all CATEGORYMEM entries (TunerPro nesting path).
function catPathOf(el, categories) {
  const mems = Array.from(el.querySelectorAll("CATEGORYMEM"));
  mems.sort((a, b) => intAttr(a, "index", 0) - intAttr(b, "index", 0));
  const out = [];
  mems.forEach((m) => { const nm = categories[intAttr(m, "category", 1) - 1]; if (nm != null) out.push(nm); });
  return out;
}

export function parseXdf(text) {
  if (/^\s*[\x00-\x08]/.test(text) || text.charCodeAt(0) === 0x05) {
    throw new Error("This XDF looks encrypted (TunerPro-protected). Please provide a decrypted XDF.");
  }
  const doc = new DOMParser().parseFromString(text, "text/xml");
  const root = doc.querySelector("XDFFORMAT");
  if (!root) throw new Error("Not a valid XDF file (missing XDFFORMAT).");
  const header = root.querySelector("XDFHEADER");
  const title = header && header.querySelector("deftitle") ? header.querySelector("deftitle").textContent : "Untitled";
  const categories = {};
  (header ? header.querySelectorAll("CATEGORY") : []).forEach((c) => { categories[intAttr(c, "index", 0)] = attr(c, "name", "Category"); });
  const baseEl = header ? header.querySelector("BASEOFFSET") : null;
  const baseOffset = baseEl ? intAttr(baseEl, "offset", 0) : 0;
  const baseSubtract = baseEl ? intAttr(baseEl, "subtract", 0) : 0;

  const tables = Array.from(root.querySelectorAll("XDFTABLE")).map((t, i) => {
    const axes = { x: null, y: null, z: null };
    t.querySelectorAll("XDFAXIS").forEach((a) => { const id = attr(a, "id"); if (id) axes[id] = parseAxis(a); });
    const z = axes.z;
    return {
      kind: "table",
      uid: attr(t, "uniqueid", "t" + i),
      title: t.querySelector("title") ? t.querySelector("title").textContent : "Table " + i,
      category: categoryName(t, categories),
      catPath: catPathOf(t, categories),
      x: axes.x, y: axes.y, z,
      rows: z ? (z.rowCount || 1) : 1,
      cols: z ? (z.colCount || 1) : 1,
      little: z ? endianOf(z.typeFlags) : false,
      outputType: z ? z.outputType : 1,
      zDecimals: z && z.decimals != null ? z.decimals : null,
      zMin: z && Number.isFinite(z.min) ? z.min : null,
      zMax: z && Number.isFinite(z.max) ? z.max : null,
    };
  });

  const byId = {};
  tables.forEach((t) => { byId[normId(t.uid)] = t; byId[t.uid] = t; });

  const constants = Array.from(root.querySelectorAll("XDFCONSTANT")).map((c, i) => {
    const ed = c.querySelector("EMBEDDEDDATA");
    const mathEl = c.querySelector("MATH");
    const flags = ed ? intAttr(ed, "mmedtypeflags", 0) : 0;
    return {
      kind: "constant",
      uid: attr(c, "uniqueid", "c" + i),
      title: c.querySelector("title") ? c.querySelector("title").textContent : "Constant " + i,
      category: categoryName(c, categories),
      catPath: catPathOf(c, categories),
      address: ed ? intAttr(ed, "mmedaddress", null) : null,
      elemBits: ed ? intAttr(ed, "mmedelementsizebits", 8) : 8,
      typeFlags: flags,
      little: endianOf(flags),
      eq: makeEquation(mathEl ? attr(mathEl, "equation", "X") : "X"),
    };
  });

  const patches = Array.from(root.querySelectorAll("XDFPATCH")).map((p, i) => {
    const entries = Array.from(p.querySelectorAll("XDFPATCHENTRY")).map((e) => ({
      address: intAttr(e, "address", 0),
      size: intAttr(e, "datasize", 0),
      patch: hexBytes(attr(e, "patchdata")),
      base: hexBytes(attr(e, "basedata")),
      name: attr(e, "name", null),
    }));
    return {
      kind: "patch",
      uid: attr(p, "uniqueid", "p" + i),
      title: p.querySelector("title") ? p.querySelector("title").textContent : "Patch " + i,
      category: categoryName(p, categories),
      catPath: catPathOf(p, categories),
      entries,
    };
  });

  const orderEls = Array.from(root.querySelectorAll("XDFTABLE, XDFCONSTANT, XDFPATCH"));
  const orderMap = {};
  orderEls.forEach((el, i) => { const id = normId(attr(el, "uniqueid")); if (id != null) orderMap[id] = i; });
  [...tables, ...constants, ...patches].forEach((it) => { it.order = orderMap[normId(it.uid)] != null ? orderMap[normId(it.uid)] : 0; });

  return { title, categories, tables, constants, patches, byId, baseOffset, baseSubtract };
}

const hexBytes = (h) => {
  const s = String(h || "").replace(/[^0-9a-fA-F]/g, "");
  const out = new Uint8Array(Math.floor(s.length / 2));
  for (let i = 0; i < out.length; i++) out[i] = parseInt(s.substr(i * 2, 2), 16);
  return out;
};

// "patched" (bytes == patchdata), "unpatched" (bytes == basedata), or "modified".
export function patchState(u8, patch, baseOffset = 0) {
  let patched = true, isBase = true, hasPatch = false, hasBase = false;
  for (const e of patch.entries) {
    const off = e.address + baseOffset;
    const n = e.size || Math.max(e.patch.length, e.base.length);
    for (let i = 0; i < n; i++) {
      const cur = u8[off + i];
      if (i < e.patch.length) { hasPatch = true; if (cur !== e.patch[i]) patched = false; }
      if (i < e.base.length) { hasBase = true; if (cur !== e.base[i]) isBase = false; }
    }
  }
  if (hasPatch && patched) return "patched";
  if (hasBase && isBase) return "unpatched";
  return "modified";
}

// apply=true writes patchdata; apply=false restores basedata. Returns a new Uint8Array.
export function applyPatch(u8, patch, apply, baseOffset = 0) {
  const out = new Uint8Array(u8);
  for (const e of patch.entries) {
    const src = apply ? e.patch : e.base;
    const off = e.address + baseOffset;
    const n = Math.min(e.size || src.length, src.length);
    for (let i = 0; i < n; i++) out[off + i] = src[i];
  }
  return out;
}

function readRaw(view, off, bytes, signed, little) {
  if (off < 0 || off + bytes > view.byteLength) return 0;
  if (bytes === 1) return signed ? view.getInt8(off) : view.getUint8(off);
  if (bytes === 2) return signed ? view.getInt16(off, little) : view.getUint16(off, little);
  return signed ? view.getInt32(off, little) : view.getUint32(off, little);
}
function writeRaw(view, off, bytes, val, little) {
  if (off < 0 || off + bytes > view.byteLength) return;
  const v = Math.round(val);
  if (bytes === 1) view.setUint8(off, v & 0xff);
  else if (bytes === 2) view.setUint16(off, v & 0xffff, little);
  else view.setUint32(off, v >>> 0, little);
}

// TunerPro cell strides. mmedminorstridebits = bits between columns, mmedmajorstridebits =
// bits between rows; 0/negative means "packed" (natural element size / row width). A single-row
// (1×N) table that declares a positive major stride spaces its N columns by that stride — verified
// on Honda Keihin "Corte de Ignição/Injeção" (major=80bits → 10-byte gap between the 2 cells).
function cellStrides(z, rows, cols, elemBytes) {
  const major = z && z.majorStride ? z.majorStride : 0;
  const minor = z && z.minorStride ? z.minorStride : 0;
  let colStride = minor > 0 ? Math.round(minor / 8) : elemBytes;
  const rowStride = major > 0 ? Math.round(major / 8) : cols * colStride;
  if (rows === 1 && major > 0) colStride = Math.round(major / 8);
  return { rowStride, colStride };
}

// TunerPro per-cell equation override: <MATH row="R" col="C" equation="..."> (1-indexed).
function eqFor(o, r0, c0) {
  if (o && o.eqOverrides && o.eqOverrides.length) {
    for (const ov of o.eqOverrides) {
      const rok = ov.row == null || ov.row === r0 + 1;
      const cok = ov.col == null || ov.col === c0 + 1;
      if (rok && cok) return ov.eq;
    }
  }
  return o.eq;
}

// Read a table's z-grid (display values) + resolved axis labels.
export function readTable(u8, tbl, opts) {
  const view = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  const base = opts.baseOffset || 0;
  const byId = opts.byId || {};
  const z = tbl.z;
  const bytes = Math.max(1, Math.round((z ? z.elemBits : 8) / 8));
  const rows = tbl.rows, cols = tbl.cols;
  const signed = isSigned(z ? z.typeFlags : 0);
  const little = opts.little != null ? !!opts.little : endianOf(z ? z.typeFlags : 0);
  const colMajor = colMajorOf(z ? z.typeFlags : 0);
  const addr = (z && z.address != null ? z.address : 0) + base;

  const { rowStride, colStride } = cellStrides(z, rows, cols, bytes);
  const grid = [], raw = [];
  for (let r = 0; r < rows; r++) {
    const rowV = [], rowR = [];
    for (let c = 0; c < cols; c++) {
      const off = colMajor ? addr + (c * rows + r) * bytes : addr + r * rowStride + c * colStride;
      const rv = readRaw(view, off, bytes, signed, little);
      rowR.push(rv);
      rowV.push(z ? eqFor(z, r, c).fwd(rv) : rv);
    }
    grid.push(rowV); raw.push(rowR);
  }

  const readSeq = (address, count, bits, sgn, le, zobj, dp) => {
    const ab = Math.max(1, Math.round(bits / 8));
    const rnd = (v) => (dp != null && Number.isFinite(v) ? Math.round(v * Math.pow(10, dp)) / Math.pow(10, dp) : v);
    return Array.from({ length: count }, (_, i) => rnd(eqFor(zobj, i, 0).fwd(readRaw(view, address + base + i * ab, ab, sgn, le))));
  };
  const numOrStr = (v) => { const n = Number(v); return v !== "" && v != null && Number.isFinite(n) ? n : v; };
  const axisLabels = (axis, count) => {
    if (!axis) return Array.from({ length: count }, (_, i) => i);
    if (axis.labels && axis.labels.length && axis.labels.some((v) => v != null && v !== "")) {
      return axis.labels.slice(0, count).map((v, i) => (v == null || v === "" ? i : numOrStr(v)));
    }
    if (axis.linkObjId && byId[axis.linkObjId]) {
      const lt = byId[axis.linkObjId];
      if (lt.z && lt.z.address != null) return readSeq(lt.z.address, count, lt.z.elemBits, isSigned(lt.z.typeFlags), endianOf(lt.z.typeFlags), lt.z, lt.z.decimals);
    }
    if (axis.address != null) return readSeq(axis.address, count, axis.elemBits, isSigned(axis.typeFlags), axis.typeFlags ? endianOf(axis.typeFlags) : little, axis, axis.decimals);
    return Array.from({ length: count }, (_, i) => i);
  };
  // Resolve the display decimals for an axis, mirroring axisLabels(): a linked axis
  // (embedinfo linkobjid) inherits the linked table's z decimalpl (e.g. Ignition Main's
  // X links to "Tabel TPS" whose z has decimalpl=1 -> labels show 0.0, 1.0, 41.0).
  const axisDecimals = (axis) => {
    if (!axis) return null;
    if (axis.decimals != null) return axis.decimals;
    if (axis.linkObjId && byId[axis.linkObjId]) {
      const lt = byId[axis.linkObjId];
      if (lt.z && lt.z.decimals != null) return lt.z.decimals;
    }
    return null;
  };

  return {
    grid, raw, rows, cols, bytes, signed, addr, little,
    xLabels: axisLabels(tbl.x, cols),
    yLabels: rows > 1 ? axisLabels(tbl.y, rows) : [],
    xDecimals: axisDecimals(tbl.x),
    yDecimals: axisDecimals(tbl.y),
  };
}

export function writeTable(u8, tbl, displayGrid, opts) {
  const out = new Uint8Array(u8);
  const view = new DataView(out.buffer);
  const z = tbl.z;
  const bytes = Math.max(1, Math.round((z ? z.elemBits : 8) / 8));
  const base = opts.baseOffset || 0;
  const little = opts.little != null ? !!opts.little : endianOf(z ? z.typeFlags : 0);
  const colMajor = colMajorOf(z ? z.typeFlags : 0);
  const addr = (z && z.address != null ? z.address : 0) + base;
  const cols = tbl.cols, rows = tbl.rows;
  const { a, b } = z ? z.eq : { a: 1, b: 0 };
  const { rowStride, colStride } = cellStrides(z, rows, cols, bytes);
  const maxU = bytes === 1 ? 0xff : bytes === 2 ? 0xffff : 0xffffffff;
  for (let r = 0; r < displayGrid.length; r++) {
    for (let c = 0; c < displayGrid[r].length; c++) {
      let rawv = a ? (displayGrid[r][c] - b) / a : displayGrid[r][c];
      rawv = Math.round(rawv);
      if (rawv < 0) rawv = 0; if (rawv > maxU) rawv = maxU;
      const off = colMajor ? addr + (c * rows + r) * bytes : addr + r * rowStride + c * colStride;
      writeRaw(view, off, bytes, rawv, little);
    }
  }
  return out;
}

export function readConstant(u8, cst, opts) {
  const view = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  const bytes = Math.max(1, Math.round(cst.elemBits / 8));
  const little = cst.typeFlags != null && endianOf(cst.typeFlags) ? true : !!opts.little;
  const rv = readRaw(view, (cst.address || 0) + (opts.baseOffset || 0), bytes, isSigned(cst.typeFlags), little);
  return cst.eq.fwd(rv);
}
